import {
  BatchWriteItemCommand,
  DynamoDBClient,
  type WriteRequest,
} from "@aws-sdk/client-dynamodb";
import { fetchAuthSession } from "aws-amplify/auth";
import type { CloudConfig } from "./cloudConfig";

const K_FLUSH_INTERVAL_MS = 10_000;
const K_FLUSH_SIZE_THRESHOLD = 25;
const K_MAX_BATCH_SIZE = 25;
const K_MAX_RETRIES = 5;

/**
 * Small Dynamo batch queue shared by observational event/transcript writers.
 * It owns transport/retry only; each service remains responsible for its item
 * keys and attributes.
 */
export class AwsBatchWriteQueue {
  private readonly m_pending: WriteRequest[] = [];
  private readonly m_client: DynamoDBClient;
  private readonly m_timer: number;
  private m_flushPromise: Promise<void> | null = null;

  constructor(
    private readonly m_tableName: string,
    config: CloudConfig,
  ) {
    this.m_client = new DynamoDBClient({
      region: config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const credentials = session.credentials;
        if (!credentials?.accessKeyId || !credentials.secretAccessKey) {
          throw new Error("[AwsBatchWriteQueue] No AWS credentials available.");
        }
        return {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
          expiration: credentials.expiration,
        };
      },
    });
    this.m_timer = window.setInterval(() => {
      void this.flushPending();
    }, K_FLUSH_INTERVAL_MS);
  }

  enqueue(request: WriteRequest): void {
    this.m_pending.push(request);
    if (this.m_pending.length >= K_FLUSH_SIZE_THRESHOLD) {
      void this.flushPending();
    }
  }

  flushPending(): Promise<void> {
    if (this.m_flushPromise) return this.m_flushPromise;
    if (this.m_pending.length === 0) return Promise.resolve();

    const batch = this.m_pending.splice(0, this.m_pending.length);
    this.m_flushPromise = this.persist(batch)
      .catch((error: unknown) => {
        // Preserve order and retry on the next interval instead of dropping
        // records because of a transient credential/network failure.
        this.m_pending.unshift(...batch);
        console.warn("[AwsBatchWriteQueue] batch write failed; queued for retry:", error);
      })
      .finally(() => {
        this.m_flushPromise = null;
        if (this.m_pending.length >= K_FLUSH_SIZE_THRESHOLD) {
          void this.flushPending();
        }
      });
    return this.m_flushPromise;
  }

  dispose(): Promise<void> {
    window.clearInterval(this.m_timer);
    return this.flushPending().finally(() => this.m_client.destroy());
  }

  private async persist(requests: ReadonlyArray<WriteRequest>): Promise<void> {
    for (let offset = 0; offset < requests.length; offset += K_MAX_BATCH_SIZE) {
      let pending = requests.slice(offset, offset + K_MAX_BATCH_SIZE);
      for (let attempt = 0; attempt < K_MAX_RETRIES && pending.length > 0; attempt += 1) {
        const response = await this.m_client.send(
          new BatchWriteItemCommand({
            RequestItems: { [this.m_tableName]: pending },
          }),
        );
        pending = response.UnprocessedItems?.[this.m_tableName] ?? [];
      }
      if (pending.length > 0) {
        throw new Error(`${pending.length} DynamoDB items remained unprocessed.`);
      }
    }
  }
}
